/**
 * Registry projection: `@publisher/name → one App`.
 *
 * The index tolerates duplicate ids by design — it's keyed by path, and a
 * CRDT can't enforce global uniqueness at write time (two offline clients can
 * both install `@acme/foo`). But the global-registry consumers *need* one
 * winner per id: hosting must route a URL to a single App, and agents must
 * not see two identically-named tools/skills.
 *
 * Projection time is the only place uniqueness is both possible and
 * necessary, so it's resolved here with a **deterministic winner rule** every
 * replica computes identically from the same index:
 *
 *  1. launchable beats broken (an entry with a parsed manifest wins over one
 *     whose manifest failed to parse);
 *  2. shallower path beats deeper (the standard top-level `Apps/` home wins
 *     over a copy buried in a working directory);
 *  3. lexicographic path order breaks the remainder.
 *
 * Collisions are surfaced, not hidden — installs overwrite-and-roll-back
 * cleanly, so a collision is a visible annoyance for the user to resolve,
 * never corruption.
 */

import type { AppIndexEntry } from "./app-index";

export interface AppIdCollision {
  id: string;
  /** Path of the entry the registry projected. */
  winner: string;
  /** Paths of the entries the winner shadowed. */
  losers: string[];
}

export interface AppRegistry {
  /** `@publisher/name` → the winning entry. */
  byId: Map<string, AppIndexEntry>;
  /** Every id that had more than one candidate — surface these in UI. */
  collisions: AppIdCollision[];
}

const depthOf = (p: string) => p.split("/").length;

/** The winner rule. Negative ⇒ `a` wins. Total and replica-deterministic. */
function compareCandidates(a: AppIndexEntry, b: AppIndexEntry): number {
  const aBroken = a.manifest ? 0 : 1;
  const bBroken = b.manifest ? 0 : 1;
  if (aBroken !== bBroken) return aBroken - bBroken;
  const byDepth = depthOf(a.path) - depthOf(b.path);
  if (byDepth !== 0) return byDepth;
  return a.path < b.path ? -1 : a.path > b.path ? 1 : 0;
}

export function projectAppRegistry(
  entries: Iterable<AppIndexEntry>,
): AppRegistry {
  const candidates = new Map<string, AppIndexEntry[]>();
  for (const entry of entries) {
    if (!entry.id) continue; // broken manifests without an id can't project
    const list = candidates.get(entry.id) ?? [];
    list.push(entry);
    candidates.set(entry.id, list);
  }

  const byId = new Map<string, AppIndexEntry>();
  const collisions: AppIdCollision[] = [];
  for (const [id, list] of candidates) {
    list.sort(compareCandidates);
    const winner = list[0]!;
    byId.set(id, winner);
    if (list.length > 1) {
      collisions.push({
        id,
        winner: winner.path,
        losers: list.slice(1).map((e) => e.path),
      });
    }
  }
  collisions.sort((a, b) => a.id.localeCompare(b.id));
  return { byId, collisions };
}
